import type {
    CarDataType,
    CompareMotorizationType,
} from "../../../common-deprecated/features/compare/utils/newCarCompareTypes";
import { NcCompareContext } from "../../../common-deprecated/types/NewCarCompareTypes";
import {
    type AEMTrackingShortEventType,
    trackEvent,
    updateTrackingBaseLayer,
} from "../../../common-deprecated/utils/tracking";
import { getEngineName } from "./formatters/nameFormatters";

const trackingDefaults: Omit<AEMTrackingShortEventType, "action"> = {
    name: "componentevent",
    component: "grade-compare",
    group: "product-general",
};

let compareContext: NcCompareContext = NcCompareContext.Original;

/** Based on CarDataType */
export type TrackingCarNameType = {
    modelName: string;
    gradeName: string;
    bodyTypeName: string;
    configurationId?: string;
};

const getCarName = (car: TrackingCarNameType): string => {
    switch (compareContext) {
        case NcCompareContext.Leasing:
            return car.configurationId || "";
        case NcCompareContext.Original:
        default:
            return `${car.modelName} ${car.gradeName} - ${car.bodyTypeName}`;
    }
};

export const setNcCompareTrackingContext = (context: NcCompareContext): void => {
    compareContext = context;
    switch (context) {
        case NcCompareContext.Original:
            trackingDefaults.component = "grade-compare";
            trackingDefaults.group = "product-general";
            break;
        case NcCompareContext.Leasing:
            trackingDefaults.component = "lease-compare";
            trackingDefaults.group = "finance";
            break;
    }
};

export const updateTrackingLayerVersioning = (): void => {
    updateTrackingBaseLayer({
        page: {
            ...((window.tngaDataLayer?.page as Record<string, unknown>) || {}),
            versions: {
                ...((window.tngaDataLayer?.page?.versions as Record<string, unknown>) || {}),
                "dxp-webcarconfig": commithash,
            },
        },
    });
};

// ----------------------------------------------------------------------
// Events
// ----------------------------------------------------------------------

export const trackCompareOpen = (columnCount: number): void => {
    trackEvent({
        ...trackingDefaults,
        action: "open-compare-panel",
        label: columnCount.toString(),
    });
};

export const trackCompareClose = (): void => {
    if (compareContext === NcCompareContext.Leasing) {
        trackEvent({
            ...trackingDefaults,
            action: "close-compare-panel",
        });
    }
};

export const trackCompareClear = (): void => {
    trackEvent({
        ...trackingDefaults,
        action: "clear-all",
    });
};

export const trackCompareGradeOpen = (): void => {
    trackEvent({
        ...trackingDefaults,
        action: compareContext === NcCompareContext.Leasing ? "modify-offer-menu" : "modify-grade-menu",
        label: "open",
    });
};

export const trackCompareGradeSelect = (action: "add" | "modify", car: TrackingCarNameType): void => {
    trackEvent({
        ...trackingDefaults,
        action: `${action}-${compareContext === NcCompareContext.Leasing ? "compare" : "grade"}`,
        label: car ? getCarName(car) : undefined,
    });
};

export const trackCompareGradeRemove = (car: CarDataType): void => {
    trackEvent({
        ...trackingDefaults,
        action: compareContext === NcCompareContext.Leasing ? "remove-lease-offer" : "remove-grade",
        label: car ? getCarName({ ...car, configurationId: car.configuration?.id }) : undefined,
    });
};

export const trackCompareEngineOpen = (): void => {
    trackEvent({
        ...trackingDefaults,
        action: "engine-menu",
        label: "open",
    });
};

export const trackCompareEngineSelect = (car: TrackingCarNameType, engine: CompareMotorizationType): void => {
    trackEvent({
        ...trackingDefaults,
        action: "change-engine",
        label: `${getCarName(car)} : ${getEngineName(engine)}`,
    });
};

export const trackComparePdf = (): void => {
    trackEvent({
        ...trackingDefaults,
        name: compareContext === NcCompareContext.Leasing ? "ctaevent" : trackingDefaults.name,
        action: compareContext === NcCompareContext.Leasing ? "download-lease-comparison" : "download-grade-comparison",
        label: "pdf",
    });
};

export const trackCompareCta = (label: "view-car-details" | "carconfigurator" | "lease-detail"): void => {
    trackEvent({
        ...trackingDefaults,
        name: "ctaevent",
        action: "click-cta",
        label,
    });
};

export const trackCompareToggleSpecs = (label: "see-all-specs" | "see-differences-only"): void => {
    if (compareContext === NcCompareContext.Leasing) {
        trackEvent({
            ...trackingDefaults,
            action: "click-tab",
            label,
        });
    }
};

export const trackCompareExpandSpecs = (label: string): void => {
    if (compareContext === NcCompareContext.Leasing) {
        trackEvent({
            ...trackingDefaults,
            action: "expand-specifications",
            label,
        });
    }
};
